import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';

/**
 * Windy Map Forecast API のキー。ビルド時に VITE_WINDY_KEY から埋め込む。
 * 未設定なら地図を読み込まない（＝ライセンス未整備の環境で誤って動かない）。
 * 検証用の Testing キーは開発専用で本番に使ってはいけない。詳細は docs/WINDY_LICENSE.md。
 */
const WINDY_KEY     = import.meta.env.VITE_WINDY_KEY as string | undefined;
const PREDICT_HOURS = 3;
// Windy のライブラリは id="windy" の div を決め打ちで探す。
// 他の id を container に渡すと "Missing <div id=\"windy\"></div>" で初期化に失敗する。
// 1ページに1つしか置けない点にも注意（公式ドキュメント）。
const CONTAINER_ID  = 'windy';

// サーバーローカルに置いた Leaflet 1.4.0（libBoot.js が要求するバージョン）。
// CSS は読み込まない：Windy 公式が「leaflet の CSS は不具合があるので使わず、
// Windy 側が修正済みのスタイルを自前で読む」としており、こちらで 1.4.0 の CSS を
// 足すとアプリ本体の Leaflet 1.9.4 のスタイルを上書きしてしまう。
const LOCAL_LEAFLET_JS  = '/lib/leaflet-1.4.0.js';
const WINDY_BOOT_URL    = 'https://api.windy.com/assets/map-forecast/libBoot.js';

/**
 * Windy ロゴを確実に見える位置へ出すためのスタイル。
 *
 * Windy の規約とライブラリのコンソール出力は「ロゴを削除・隠蔽してはならない。
 * クリック可能なリンクとして windy.com へ遷移すること」を求めている。
 * ところが既定では #logo-wrapper が高さ0・bottom:518px（地図の上端）に置かれ、
 * z-index も auto のため気象レイヤーの下に潜って見えなくなる。
 * そこで左下へ固定し、地図レイヤーより前面に出す。透明度は下げない。
 */
const LOGO_CSS = `
#windy #logo-wrapper { z-index: 1000 !important; }
#windy #logo {
  z-index: 1001 !important;
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
}
`;
const LOGO_STYLE_ID = 'windy-logo-style';

export interface WindyVessel {
  id:      string;
  name:    string;
  color:   string;
  lat:     number | null;
  lng:     number | null;
  speed:   number;
  heading: number;
  trail:   { ts: number; lat: number; lng: number }[];
}

interface Props {
  vessels:     WindyVessel[];
  currentTime: number;
  visible:     boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyL = any;

declare global {
  interface Window {
    windyInit: (opts: Record<string, unknown>, cb: (api: { map: AnyL }) => void) => void;
    L: AnyL;
  }
}

const WindyMap: React.FC<Props> = ({ vessels, currentTime, visible }) => {
  const readyRef       = useRef(false);
  const mapRef         = useRef<AnyL>(null);
  const layerRef       = useRef<L.LayerGroup | null>(null);
  const windyLRef      = useRef<AnyL>(null); // Windy の Leaflet インスタンス
  const vesselsRef     = useRef(vessels);
  const currentTimeRef = useRef(currentTime);
  const [status, setStatus] = useState<string>('読み込み中...');

  useEffect(() => { vesselsRef.current = vessels; }, [vessels]);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  function renderVessels() {
    const layer = layerRef.current;
    const WL    = windyLRef.current ?? L; // Windy の Leaflet を優先
    if (!layer) return;
    layer.clearLayers();

    const vs = vesselsRef.current;
    const ct = currentTimeRef.current;

    vs.forEach(v => {
      if (v.lat == null || v.lng == null) return;

      const pts = v.trail
        .filter(p => p.ts <= ct)
        .map(p => [p.lat, p.lng] as [number, number]);
      if (pts.length > 1) {
        WL.polyline(pts, { color: v.color, weight: 2.5, opacity: 0.9 }).addTo(layer);
      }

      const hdg  = v.heading ?? 0;
      const icon = WL.divIcon({
        html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;transform:rotate(${hdg}deg);filter:drop-shadow(0 2px 4px rgba(0,0,0,.6))"><svg viewBox="0 0 24 24" width="26" height="26"><path d="M12 2L7 20l5-5 5 5z" fill="${v.color}" stroke="white" stroke-width="1.2" stroke-linejoin="round"/></svg></div>`,
        className: '', iconSize: [28, 28], iconAnchor: [14, 14],
      });

      WL.marker([v.lat, v.lng], { icon })
        .bindPopup(`<div style="font-family:sans-serif;min-width:120px"><b>${v.name}</b><br><span style="color:#64748b;font-size:12px">速度: ${v.speed > 0 ? v.speed.toFixed(1) + ' kt' : '停止中'}<br>針路: ${hdg.toFixed(0)}°</span></div>`)
        .addTo(layer);

      if (v.speed > 0.5) {
        const rad     = hdg * Math.PI / 180;
        const nmTotal = v.speed * PREDICT_HOURS;
        const dLat    = (nmTotal / 60) * Math.cos(rad);
        const dLng    = (nmTotal / 60) * Math.sin(rad) / Math.cos(v.lat * Math.PI / 180);
        const pLat    = v.lat + dLat;
        const pLng    = v.lng + dLng;

        WL.polyline([[v.lat, v.lng], [pLat, pLng]], {
          color: v.color, weight: 2, opacity: 0.55, dashArray: '10 7',
        }).addTo(layer);

        WL.circleMarker([pLat, pLng], {
          radius: 5, color: v.color, fillColor: v.color, fillOpacity: 0.65, weight: 2,
        })
          .bindPopup(`<div style="font-family:sans-serif;font-size:12px"><b>${v.name}</b><br><span style="color:#64748b">${PREDICT_HOURS}時間後の予測位置</span></div>`)
          .addTo(layer);
      }
    });
  }

  useEffect(() => {
    if (!readyRef.current) return;
    renderVessels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessels, currentTime]);

  useEffect(() => {
    if (visible && mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize?.(), 150);
    }
  }, [visible]);

  function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // 既にロード済みか確認
      if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load: ${src}`));
      document.head.appendChild(s);
    });
  }

  // Windy 初期化（初回マウント時のみ）
  useEffect(() => {
    if (readyRef.current) return;
    let cancelled = false;

    async function init() {
      try {
        if (!WINDY_KEY) {
          setStatus('Windy の API キーが未設定です（VITE_WINDY_KEY）');
          return;
        }

        // Step 2: Leaflet 1.4.0 JS → window.L を上書き
        if (typeof window.L?.CanvasLayer === 'undefined') {
          setStatus('Leaflet 1.4.0 を読み込み中...');
          await loadScript(LOCAL_LEAFLET_JS);
        }

        if (cancelled) return;
        setStatus('Windy スクリプトを読み込み中...');

        // Step 3: libBoot.js（window.L が 1.4.0 であることが必要）
        await loadScript(WINDY_BOOT_URL);

        if (cancelled) return;

        if (typeof window.windyInit !== 'function') {
          setStatus('エラー: windyInit が定義されていません');
          return;
        }

        setStatus('Windy を初期化中...');

        // ロゴを確実に見せるスタイルを一度だけ入れる（規約上の表示義務）
        if (!document.getElementById(LOGO_STYLE_ID)) {
          const st = document.createElement('style');
          st.id = LOGO_STYLE_ID;
          st.textContent = LOGO_CSS;
          document.head.appendChild(st);
        }

        // Step 4: windyInit
        window.windyInit(
          {
            key:       WINDY_KEY,
            verbose:   true,
            lat:       34.0,
            lng:       136.0,
            zoom:      6,
            container: CONTAINER_ID,
          },
          (api) => {
            if (cancelled) return;
            windyLRef.current = window.L; // Windy が使う Leaflet インスタンス
            mapRef.current    = api.map;
            layerRef.current  = window.L.layerGroup().addTo(api.map);
            readyRef.current  = true;
            setStatus('');
            renderVessels();
          },
        );
      } catch (e) {
        if (!cancelled) setStatus(`エラー: ${e}`);
        console.error('[WindyMap]', e);
      }
    }

    setTimeout(init, 300);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (visible && mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize?.(), 150);
    }
  }, [visible]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#1a1a2e' }}>
      <div id={CONTAINER_ID} style={{ width: '100%', height: '100%' }} />

      {status && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff',
          padding: '16px 24px', borderRadius: 10,
          fontSize: 14, textAlign: 'center', zIndex: 10, maxWidth: 380,
        }}>
          <div style={{ marginBottom: 8, fontSize: 18 }}>☁️</div>
          {status}
        </div>
      )}
    </div>
  );
};

export default WindyMap;
